import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdClose } from "react-icons/md";
import { FaCar, FaClock, FaDollarSign, FaUsers } from "react-icons/fa";

const VehicleResultsModal = ({ isOpen, onClose, onSelect, searchData }) => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);

  const mockVehicles = [
    {
      id: 1,
      name: "Toyota Corolla",
      type: "Economy",
      location: `${searchData?.pickupLocation || 'Downtown'} → ${searchData?.dropoffLocation || 'Airport'}`,
      time: searchData?.pickupTime || "10:00 AM",
      capacity: "4 passengers",
      price: "$45/day",
      departureDate: searchData?.pickupDate || "2024-01-15"
    },
    {
      id: 2,
      name: "Honda Civic",
      type: "Compact", 
      location: `${searchData?.pickupLocation || 'Downtown'} → ${searchData?.dropoffLocation || 'Airport'}`,
      time: searchData?.pickupTime || "10:00 AM",
      capacity: "4 passengers",
      price: "$55/day",
      departureDate: searchData?.pickupDate || "2024-01-15"
    },
    {
      id: 3,
      name: "BMW X5",
      type: "Luxury SUV",
      location: `${searchData?.pickupLocation || 'Downtown'} → ${searchData?.dropoffLocation || 'Airport'}`,
      time: searchData?.pickupTime || "10:00 AM", 
      capacity: "7 passengers",
      price: "$120/day",
      departureDate: searchData?.pickupDate || "2024-01-15"
    },
    {
      id: 4,
      name: "Ford Mustang",
      type: "Sports Car",
      location: `${searchData?.pickupLocation || 'Downtown'} → ${searchData?.dropoffLocation || 'Airport'}`,
      time: searchData?.pickupTime || "10:00 AM",
      capacity: "2 passengers", 
      price: "$95/day",
      departureDate: searchData?.pickupDate || "2024-01-15"
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setVehicles(mockVehicles);
    }
  }, [isOpen]);

  const handleSelect = (vehicle) => {
    onSelect(vehicle);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[800px] max-h-[90vh] overflow-hidden bg-white/95 dark:bg-navy-800/95 backdrop-blur-sm border border-white/20 dark:border-navy-600/50 shadow-2xl">
        <div className="bg-[#5a88c6] shadow-lg p-6 rounded-t-[20px] relative overflow-hidden sticky top-0 z-20">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3">
                <FaCar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Available Vehicles</h3>
                <p className="text-white/80 text-sm">
                  {searchData?.pickupLocation} → {searchData?.dropoffLocation} • {searchData?.pickupDate}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Searching for vehicles...</p>
              <p className="text-slate-500 dark:text-slate-500 text-sm">Please wait while we find the best options</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-700 dark:text-white font-semibold mb-4">
                Found {vehicles.length} vehicles for your search
              </p>
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="group p-6 rounded-2xl bg-gradient-to-r from-white to-slate-50 dark:from-navy-800 dark:to-navy-700 border-2 border-slate-200 dark:border-navy-600 hover:border-green-400 dark:hover:border-green-400 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handleSelect(vehicle)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-2xl bg-[#5a88c6] shadow-lg">
                        <FaCar className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-800 dark:text-white">{vehicle.name}</h4>
                        <p className="text-slate-600 dark:text-slate-400">{vehicle.type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{vehicle.price}</p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">per day</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <FaCar className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{vehicle.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaClock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{vehicle.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaUsers className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{vehicle.capacity}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button className="px-6 py-2 bg-[#5a88c6] text-white rounded-xl font-semibold hover:bg-[#4a7bb6] transition-all duration-200 group-hover:scale-105">
                      Select Vehicle
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default VehicleResultsModal;